<template>
  <SidebarNavigation :uri="uri">
    <div class="w-full mx-auto">
      <h2 class="flex justify-center font-bold text-xl sm:text-2xl mb-4 sm:mb-6 border-b-2 pb-3 sm:pb-4 px-4 sm:px-0">{{ __('Floating Buttons Generator') }}</h2>
            <div class="md:mt-10 sm:mt-3 justify-center flex flex-col lg:flex-row px-4 sm:px-6 lg:px-8 gap-6 lg:gap-12 w-full mx-auto card-properties">
              <div class="card-form w-full lg:w-1/2 mx-auto">
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                  <!-- Color Selection Section -->
                  <div class="mb-6 pb-6 border-b border-gray-200">
                    <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <icon name="palette" class="w-5 h-5 text-green-600" />
                      Theme Color
                    </h3>
                    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      <div v-for="(color, ci) in colors" :key="ci" class="flex flex-col items-center gap-2">
                        <input
                          :id="'button__'+color.name"
                          v-model="selected_color"
                          type="radio"
                          :value="ci"
                          name="bordered-radio"
                          class="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500"
                          @change="setButtonImage(color)"
                        />
                        <label
                          :for="'button__'+color.name"
                          :class="[
                            'w-full py-4 text-sm rounded-lg font-medium text-white cursor-pointer transition-all shadow-sm hover:shadow-md',
                            selected_color === ci ? 'ring-2 ring-green-500 ring-offset-2' : ''
                          ]"
                          :style="{background: color.code}"
                        >
                          {{ color.name }}
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Links Section -->
                  <div class="mb-6 pb-6 border-b border-gray-200">
                      <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <icon name="website" class="w-5 h-5 text-green-600" />
                        Button Links
                      </h3>
                    <div class="space-y-4">
                      <text-input
                        v-model="vcf_file_url"
                        class="w-full"
                        :label="__('vCard VCF File URL')"
                        type="url"
                        autofocus
                        autocapitalize="off"
                        placeholder="https://example.com/vcard.vcf"
                        :error="validationErrors.vcf_file_url"
                        @blur="validateVcfUrlField"
                        maxlength="255"
                      />
                      <text-input
                        v-model="referral_link_url"
                        class="w-full"
                        :label="__('Referral Link URL')"
                        type="url"
                        autofocus
                        autocapitalize="off"
                        placeholder="https://example.com/referral"
                        :error="validationErrors.referral_link_url"
                        @blur="validateReferralUrlField"
                        maxlength="255"
                      />
                    </div>
                  </div>

                  <!-- Generate Button -->
                  <div>
                    <button
                      @click="colorCodeAction()"
                      class="w-full text-white flex justify-center items-center bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 active:from-green-800 active:to-green-900 focus:outline-none rounded-lg px-6 py-3 gap-2 font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                      <icon name="code" class="w-5 h-5 text-white fill-white" />
                      Generate Widget Code
                    </button>
                    <p class="text-xs text-gray-500 text-center mt-3">
                      Generate embeddable code for your floating button widget
                    </p>
                  </div>
                </div>
              </div>
              <div class="card-items flex w-full lg:w-1/2 mx-auto py-0">
                <div class="flex flex-col gap-4 w-full bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                  <!-- Preview Header -->
                  <div class="w-full mb-4 pb-4 border-b border-gray-200">
                    <h3 class="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-2">
                      <icon name="aperture" class="w-5 h-5 text-green-600" />
                      Button Preview & Code
                    </h3>
                    <p class="text-xs text-gray-500">Preview your floating button and copy the embed code</p>
                  </div>

                  <!-- Button Preview -->
                  <div class="flex justify-center items-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 min-h-[120px]">
                    <img v-if="button_image" :src="button_image" alt="Floating button preview" class="max-w-full h-auto" />
                    <div v-else class="text-center text-gray-400">
                      <icon name="code" class="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p class="text-sm">Select a color and generate code to see preview</p>
                    </div>
                  </div>

                  <!-- Code Section -->
                  <div v-if="html_code" class="space-y-3">
                    <div class="flex items-center justify-between">
                      <h4 class="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <icon name="code" class="w-4 h-4" />
                        Widget Code
                      </h4>
                      <button
                        @click="copyCode()"
                        :disabled="copy_text_html"
                        :class="[
                          'px-4 py-2 text-sm font-medium rounded-lg transition-all shadow-sm hover:shadow-md flex items-center gap-2',
                          copy_text_html
                            ? 'bg-green-600 text-white cursor-default'
                            : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                        ]"
                      >
                        <icon v-if="!copy_text_html" name="file" class="w-4 h-4" />
                        <icon v-else name="tick" class="w-4 h-4" />
                        {{ copy_text_html || 'Copy Code' }}
                      </button>
                    </div>
                    <div class="relative">
                      <div v-if="processing_code" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                        <div class="flex flex-col items-center gap-2">
                          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                          <span class="text-sm text-gray-600">Generating code...</span>
                        </div>
                      </div>
                      <textarea
                        :value="html_code"
                        class="w-full border-2 border-gray-300 rounded-lg resize-none text-xs font-mono p-3 bg-gray-50 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        rows="8"
                        readonly
                      ></textarea>
                    </div>
                    <div v-if="copy_text_html" class="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <icon name="tick" class="w-5 h-5 text-green-600" />
                      <p class="text-sm text-green-800 font-medium">{{ copy_text_html }}</p>
                    </div>
                    <p class="text-xs text-gray-500">
                      Copy this code and paste it into your website's HTML to display the floating button
                    </p>
                  </div>
                  <div v-else class="text-center py-8 text-gray-400">
                    <icon name="code" class="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p class="text-sm">Generate code to see it here</p>
                  </div>
                </div>
              </div>
            </div>
    </div>
    <!-- Loading Process -->
    <div v-if="form.processing" class="processing-overlay">
      <div class="background" />
      <div class="loader">
        <svg width="50px" height="50px" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" style="background: none;">
          <circle cx="75" cy="50" fill="#ffffff" r="6.39718">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.875s" />
          </circle>
          <circle cx="67.678" cy="67.678" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.75s" />
          </circle>
          <circle cx="50" cy="75" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.625s" />
          </circle>
          <circle cx="32.322" cy="67.678" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.5s" />
          </circle>
          <circle cx="25" cy="50" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.375s" />
          </circle>
          <circle cx="32.322" cy="32.322" fill="#ffffff" r="4.80282">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.25s" />
          </circle>
          <circle cx="50" cy="25" fill="#ffffff" r="6.40282">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.125s" />
          </circle>
          <circle cx="67.678" cy="32.322" fill="#ffffff" r="7.99718">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="0s" />
          </circle>
        </svg>
      </div>
    </div>
    <!-- Loading Process -->
  </SidebarNavigation>
</template>

<script>
import Layout from '@/Shared/ToolLayout'
import TextInput from '@/Shared/TextInput'
import Icon from '@/Shared/Icon'
import {toPng } from 'html-to-image'
import TextareaInput from '@/Shared/TextareaInput.vue'
import SidebarNavigation from '@/Shared/SidebarNavigation.vue'

const b__l = '/images/buttons/'

export default {
    components: {
        SidebarNavigation,
        TextareaInput,
        Icon,
        TextInput,
    },
    layout: Layout,
    props: {
        title: String,
        html: String,
        uri: {
            type: String,
            required: false,
        },
    },
    remember: 'form',
    data() {
        return {
            html_code: this.html,
            colors: [
                {name: 'black', code: '#000000', image: b__l+'b1.png'},
                {name: 'green', code: '#06752d', image: b__l+'g1.png'},
                {name: 'red', code: '#af191c', image: b__l+'r1.png'},
                {name: 'blue', code: '#015b90', image: b__l+'l1.png'},
            ],
            copy_text: '',
            processing_code: '',
            copy_text_html: '',
            vcf_file_url: 'https://save-to-contacts.com/card/id/demo.vcf',
            referral_link_url: 'https://refer.direct/demo',
            validationErrors: {},
            button_image: '/images/buttons/b1.png',
            selected_color: 0,
            code: 'const foo = \'bar\';',
            step: 3,
            file_accept: '.csv,.xlsx,.tsv,.ods,.xls,.slk,.xml',
            form: this.$inertia.form({
                first_name: '',
                last_name: '',
                phones: [],
                emails: [],
                websites: [],
                addresses: [],
                logo: null,
                card_link: '',
                file_type: null,
            }),
            enable_csv_import: false,
            cardPages: [],
            print_preview: false,
            allPortraitCardDesigns: [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14],
            allLandscapeCardDesigns: [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15],
            cardPortraitDesigns: [],
            cardLandscapeDesigns: [],
            page_number_portrait: 1,
            page_number_landscape: 1,
            selected_design: { number: 1, type: 'portrait' },
            current_card: null,
            paper: {
                size: 'a4',
            },
            card_layout: 'horizontal',
            referral_layout: 'horizontal',
            refreshed_user_at: (new Date()).valueOf(),
        }
    },
    created() {
        this.setForm({
            first_name: 'John',
            last_name: 'Doe',
            phones: [{'type': 'Work', 'phone': '833-000-0000'}],
            emails: [{'type': 'Work', 'email': 'my-email@gmail.com'}],
            websites: [{'type': 'Work', 'website': 'example.com'}],
            addresses: [{'type': 'Work', 'name': 'ABC Motors', 'address': '3611 N Main St, Houston, TX 77009' }],
            logo: '',
        })
    },
    methods: {
        validateVcfUrlField() {
            if (this.vcf_file_url) {
                const result = validateURL(this.vcf_file_url)
                if (!result.valid) {
                    this.validationErrors.vcf_file_url = result.message
                } else {
                    delete this.validationErrors.vcf_file_url
                    // Normalize URL
                    if (result.normalized) {
                        this.vcf_file_url = result.normalized
                    }
                }
            } else {
                delete this.validationErrors.vcf_file_url
            }
        },
        validateReferralUrlField() {
            if (this.referral_link_url) {
                const result = validateURL(this.referral_link_url)
                if (!result.valid) {
                    this.validationErrors.referral_link_url = result.message
                } else {
                    delete this.validationErrors.referral_link_url
                    // Normalize URL
                    if (result.normalized) {
                        this.referral_link_url = result.normalized
                    }
                }
            } else {
                delete this.validationErrors.referral_link_url
            }
        },
        setButtonImage(obj){
            this.button_image = obj.image
        },
        colorCodeAction(){
            // Validate URLs before generating
            if (this.vcf_file_url) {
                this.validateVcfUrlField()
            }
            if (this.referral_link_url) {
                this.validateReferralUrlField()
            }

            if (this.validationErrors.vcf_file_url || this.validationErrors.referral_link_url) {
                if (window.$toast) {
                    window.$toast.warning('Please fix URL validation errors before generating code')
                }
                return
            }

            this.processing_code = true
            const sel_color = this.colors[this.selected_color]
            const referral_link_url = (this.referral_link_url.indexOf('://') === -1) ? 'https://' + this.referral_link_url : this.referral_link_url
            this.axios.post(this.route('load.html'),  {color: sel_color.name, vcf_file_url: this.vcf_file_url, referral_link_url}).then((response) => {
                if( response.data && response.data.html){
                    this.html_code = response.data.html
                    if (window.$toast) {
                        window.$toast.success('Widget code generated successfully!')
                    }
                }
                this.processing_code = false
            }).catch((error) => {
                console.error('Error generating widget code:', error)
                this.processing_code = false

                let errorMessage = 'Failed to generate widget code. Please try again.'
                if (error.response) {
                    if (error.response.status === 422) {
                        errorMessage = 'Validation failed. Please check your URLs.'
                    } else if (error.response.status === 500) {
                        errorMessage = 'Server error. Please try again later.'
                    } else if (error.response.data?.message) {
                        errorMessage = error.response.data.message
                    }
                } else if (error.message) {
                    if (error.message.includes('Network') || error.message.includes('timeout')) {
                        errorMessage = 'Network error. Please check your connection and try again.'
                    }
                }

                if (window.$toast) {
                    window.$toast.error(errorMessage)
                }
            })
        },
        generateVCF(){
            this.axios.post(this.route('generate.vcf'),  this.form).then((response) => {
                if( response.data && response.data.card_link){
                    this.form.card_link = response.data.card_link
                }
            }).catch((error) => {
                console.log(error)
                this.form.processing = false
            })
        },
        async copyURL() {
            try {
                await navigator.clipboard.writeText(this.form.card_link)
                this.copy_text = 'The VCF card link has been Copied!'
                if (window.$toast) {
                    window.$toast.success('URL copied to clipboard!')
                }
                this.hideCopy()
            } catch($e) {
                this.copy_text = 'Cannot copy the link!'
                if (window.$toast) {
                    window.$toast.error('Failed to copy URL. Please try again.')
                }
                this.hideCopy()
            }
        },
        async copyCode() {
            try {
                await navigator.clipboard.writeText(this.html_code)
                this.copy_text_html = 'The code has been Copied!'
                if (window.$toast) {
                    window.$toast.success('Code copied to clipboard!')
                }
                this.hideCopy()
            } catch($e) {
                this.copy_text_html = 'Cannot copy the code!'
                if (window.$toast) {
                    window.$toast.error('Failed to copy code. Please try again.')
                }
                this.hideCopy()
            }
        },
        hideCopy(){
            const that = this
            setTimeout(function(){that.copy_text = ''; that.copy_text_html = ''}, 3000)
        },
        removeEntry(key, arr){
            arr.splice(key, 1)
        },
        addNewEntry(type){
            const entry_keys = {'email': 'emails', 'phone': 'phones', 'website': 'websites', 'address': 'addresses'}
            const empty_data = {
                'address' : {'type': 'Work', 'name': '', 'address': '', 'address_2': '', 'city': '', 'region': '', 'country': '', 'zip': '' },
                'phone' : {'type': 'Home', 'phone': ''},
                'email' : {'type': 'Work', 'email': ''},
                'website' : {'type': 'Work', 'website': ''},
            }
            this.form[entry_keys[type]].push(empty_data[type])
        },
        changeTextAlign(e){
            if(this.card_layout === 'horizontal'){
                const length = e.currentTarget.value.length
                if(length > 14){
                    this.$refs.user__name.style.textAlign = 'center'
                    this.$refs.user__name.style.width = '120px'
                }else{
                    this.$refs.user__name.style.textAlign = 'left'
                    this.$refs.user__name.style.width = 'auto'
                }
                this.$refs.user__email.style.paddingLeft = this.$refs.user__name.offsetLeft ? this.$refs.user__name.offsetLeft + 'px' : '29px'
            }
        },
        changeFontSize(e){
            if(this.card_layout === 'vertical'){
                const length = e.currentTarget.value.length
                // this.$refs.email_input.style.fontSize = length > 27 ? 27/length * 12 + 'px' : '12px'
            }
        },
        downloadAsPNG(referral){
            const target_selector = document.getElementsByClassName(referral ? 'card__content__referral' : 'card__content')
            const target = target_selector?target_selector[0]: null
            const gen_id = Math.floor(Date.now() /1000)
            toPng(target, {pixelRatio: 6}).then((dataUrl) => {
                const link = document.createElement('a')
                link.download = gen_id + '.' + 'png'
                link.href = dataUrl
                link.click()
            })
                .catch((err) => {
                    console.log(err)
                })
        },
        setForm(elements){
            const that = this
            Object.keys(elements).forEach(function (element) {
                that.form[element] = elements[element]
            })
        },
        partitionArray(array, size){
            return array.map( (e,i) => (i % size === 0) ? array.slice(i, i + size) : null ).filter( (e) => e )
        },
        withPhotosCheck(){
            if(this.form.with_photos){
                this.file_accept = '.zip'
            }
        },
        logoInputChange(e){
            if(e.target.files.length && e.target.files[0]){
                this.form.file_type = e.target.files[0].type
                const self = this
                const reader = new FileReader()
                reader.readAsDataURL( e.target.files[0] )
                reader.addEventListener('load', function(){
                    self.form.logo = reader.result
                })
            }
        },
        enableCsvImport(boolean){
            let el = document.getElementById('cardInputs'), all = el.getElementsByTagName('input'), i
            for (i = 0; i < all.length; i++) {
                if(all[i].type.toLowerCase() === 'text'){
                    all[i].disabled = boolean
                    if(boolean){
                        all[i].parentElement.classList.add('opacity-30')
                    }else{
                        all[i].parentElement.classList.remove('opacity-30')
                    }
                }
            }
        },
        fileInputChange(e) {
            this.form.processing = true
            let data = new FormData()
            const fileSel = document.getElementById('file')
            if(fileSel.files.length && fileSel.files[0]){
                data.append('file', document.getElementById('file').files[0])
                this.axios.post(this.route('import.file'), data).then(( response ) => {
                    if(response.data && response.data.csv && response.data.csv.length){
                        this.setCard(response.data.csv)
                        this.generateCardDesign()
                        this.form.processing = false
                        this.step = 2
                    }else{
                        if (window.$toast) {
                            window.$toast.error('Something went wrong while importing the file. Please try again.')
                        }
                        this.form.processing = false
                    }
                }).catch((error) => {
                    this.form.processing = false
                    console.error('File upload error:', error)

                    let errorMessage = 'Failed to import file. Please try again.'
                    if (error.response) {
                        if (error.response.status === 413) {
                            errorMessage = 'File is too large. Please use a smaller file.'
                        } else if (error.response.status === 415) {
                            errorMessage = 'Unsupported file type. Please use CSV, XLSX, or TSV format.'
                        } else if (error.response.data && error.response.data.message) {
                            errorMessage = error.response.data.message
                        }
                    } else if (error.message) {
                        if (error.message.includes('Network')) {
                            errorMessage = 'Network error. Please check your connection and try again.'
                        }
                    }

                    if (window.$toast) {
                        window.$toast.error(errorMessage)
                    }
                })
            }
        },
        setCard(data){
            this.form.cards = [...data]
            const cardData = this.form.cards.map((c, ci) => {
                const qrText = c.name + ' \n' + c.profession + ' \n' + c.location + ' \n' + c.phone + ' \n' + c.email + ' \n' + c.website
                return {name: qrText, index: ci}
            })
            this.createQr(cardData)
        },
        createQr(cardData){
            this.axios.post(this.route('create.qr'), { data: cardData }).then((response) => {
                if( response.data && response.data.images && response.data.images.length ){
                    this.setQrImageToCards(response.data.images)
                }
            }).catch((error) => {
                console.log(error)
                this.form.processing = false
            })
        },
        fileBrowse() {
            if(this.form.school_name){
                this.$refs.file.click()
            }else{
                if (window.$toast) {
                    window.$toast.warning('Please input school name first!')
                }
            }
        },
    },
}
</script>





